import axios from 'axios';
import { createAsyncThunk, createSlice, isPending, isRejected } from '@reduxjs/toolkit';
import { API_ROUTES } from '@/config/api-routes';
import { userIamUrl } from '@/config/user-iam';
import { deleteChatRoom as deleteChatRoomApi, fetchChatRoomMembers as fetchChatRoomMembersApi, removeChatRoomMember as removeChatRoomMemberApi, upsertChatRoomMember, } from '@/services/chat/chatApi';
import { readPagedRows } from '@/shared/utils/spring-api';
import { resolveAsyncThunkErrorMessage, serializeAxiosError } from './reducer.utils';
const createRoomUrl = API_ROUTES.chat.rooms;
/** IAM user API — same as {@link getUsers} in user.reducer.ts */
const usersApiUrl = () => userIamUrl('/user-management/api/admin/users');
const initialState = {
    loading: false,
    creating: false,
    mutating: false,
    membersLoading: false,
    directoryLoading: false,
    errorMessage: null,
    rooms: [],
    directoryUsers: [],
    roomMembers: [],
    lastCreated: null,
    lastDeletedRoomId: null,
};
export const getMyChatRooms = createAsyncThunk('chatRoom/fetch_mine', async () => axios.get(API_ROUTES.chat.mine), { serializeError: serializeAxiosError });
/** Admin / settings — create room with members */
export const createChatRoom = createAsyncThunk('chatRoom/create', async (payload) => axios.post(createRoomUrl, payload), { serializeError: serializeAxiosError });
/** IAM user directory for chat room / DM pickers (same API as user management list). */
export const getChatDirectoryUsers = createAsyncThunk('chatRoom/directory_users', async (params) => {
    const { page, size, sort, filter, username, email } = params ?? {};
    const q = new URLSearchParams();
    q.set('page', String(page ?? 0));
    q.set('size', String(size ?? 200));
    if (sort)
        q.set('sort', sort);
    if (filter)
        q.set('filter', filter);
    const trimmedUsername = username?.trim();
    const trimmedEmail = email?.trim();
    if (trimmedUsername)
        q.set('username', trimmedUsername);
    if (trimmedEmail)
        q.set('email', trimmedEmail);
    return axios.get(`${usersApiUrl()}?${q.toString()}`);
}, { serializeError: serializeAxiosError });
/** Member API — get or create DM with another user */
export const getOrCreateDmRoom = createAsyncThunk('chatRoom/dm', async (otherUserId) => axios.post(API_ROUTES.chat.dm(otherUserId)), { serializeError: serializeAxiosError });
export const deleteChatRoom = createAsyncThunk('chatRoom/delete', async (roomId) => {
    await deleteChatRoomApi(roomId);
    return roomId;
}, { serializeError: serializeAxiosError });
export const getChatRoomMembers = createAsyncThunk('chatRoom/fetch_members', async (roomId) => fetchChatRoomMembersApi(roomId), { serializeError: serializeAxiosError });
export const addChatRoomMember = createAsyncThunk('chatRoom/add_member', async ({ roomId, userId, role = 'MEMBER', }) => upsertChatRoomMember(roomId, userId, { userId, role }), { serializeError: serializeAxiosError });
export const removeChatRoomMember = createAsyncThunk('chatRoom/remove_member', async ({ roomId, userId }) => {
    await removeChatRoomMemberApi(roomId, userId);
    return { roomId, userId };
}, { serializeError: serializeAxiosError });
export const ChatRoomSlice = createSlice({
    name: 'chatRoom',
    initialState: initialState,
    reducers: {
        reset() {
            return initialState;
        },
        clearLastCreated(state) {
            state.lastCreated = null;
        },
        clearErrorMessage(state) {
            state.errorMessage = null;
        },
        clearLastDeletedRoom(state) {
            state.lastDeletedRoomId = null;
        },
        /** Live STOMP message — update sidebar preview / unread without waiting for REST refresh. */
        applyInboundChatMessage(state, action) {
            const { roomId, message, incrementUnread } = action.payload;
            const idx = state.rooms.findIndex((r) => r.id === roomId);
            if (idx < 0) {
                return;
            }
            const room = state.rooms[idx];
            const updated = {
                ...room,
                lastMessage: {
                    id: message.id,
                    roomId,
                    senderId: message.fromUser,
                    content: message.deleted ? '(message deleted)' : message.content,
                    createdAt: message.timestamp ?? undefined,
                },
                unreadCount: incrementUnread
                    ? (room.unreadCount ?? 0) + 1
                    : room.unreadCount,
            };
            const next = [...state.rooms];
            next.splice(idx, 1);
            next.unshift(updated);
            state.rooms = next;
        },
        clearRoomUnread(state, action) {
            const roomId = action.payload;
            state.rooms = state.rooms.map((r) => r.id === roomId ? { ...r, unreadCount: 0 } : r);
        },
        /** Live STOMP `MEMBER_ADDED` on `/topic/chat.user.{username}` — push room into sidebar. */
        upsertRoomFromMemberAdded(state, action) {
            const room = action.payload;
            if (room.id == null)
                return;
            const idx = state.rooms.findIndex((r) => r.id === room.id);
            if (idx >= 0) {
                state.rooms = state.rooms.map((r, i) => (i === idx ? { ...r, ...room } : r));
                return;
            }
            state.rooms = [room, ...state.rooms];
        },
    },
    extraReducers(builder) {
        builder
            .addCase(getMyChatRooms.fulfilled, (state, action) => {
            state.loading = false;
            state.rooms = action.payload.data ?? [];
        })
            .addCase(createChatRoom.fulfilled, (state, action) => {
            state.creating = false;
            state.lastCreated = action.payload.data;
            const created = action.payload.data;
            if (created?.id != null) {
                const idx = state.rooms.findIndex((r) => r.id === created.id);
                if (idx >= 0) {
                    state.rooms = state.rooms.map((r, i) => (i === idx ? created : r));
                }
                else {
                    state.rooms = [created, ...state.rooms];
                }
            }
        })
            .addCase(getChatDirectoryUsers.fulfilled, (state, action) => {
            state.directoryLoading = false;
            const { rows } = readPagedRows(action.payload.data, action.payload.headers);
            state.directoryUsers = rows;
        })
            .addCase(getOrCreateDmRoom.fulfilled, (state, action) => {
            state.creating = false;
            state.lastCreated = action.payload.data;
            const created = action.payload.data;
            if (created?.id != null) {
                const idx = state.rooms.findIndex((r) => r.id === created.id);
                if (idx >= 0) {
                    state.rooms = state.rooms.map((r, i) => (i === idx ? created : r));
                }
                else {
                    state.rooms = [created, ...state.rooms];
                }
            }
        })
            .addCase(deleteChatRoom.fulfilled, (state, action) => {
            state.mutating = false;
            state.lastDeletedRoomId = action.payload;
            state.rooms = state.rooms.filter((room) => room.id !== action.payload);
            state.roomMembers = [];
        })
            .addCase(getChatRoomMembers.fulfilled, (state, action) => {
            state.membersLoading = false;
            state.roomMembers = action.payload;
        })
            .addCase(addChatRoomMember.fulfilled, (state, action) => {
            state.mutating = false;
            const member = action.payload;
            const idx = state.roomMembers.findIndex((m) => m.userId === member.userId);
            if (idx >= 0) {
                state.roomMembers = state.roomMembers.map((m, i) => (i === idx ? member : m));
            }
            else {
                state.roomMembers = [...state.roomMembers, member];
            }
        })
            .addCase(removeChatRoomMember.fulfilled, (state, action) => {
            state.mutating = false;
            state.roomMembers = state.roomMembers.filter((m) => m.userId !== action.payload.userId);
        })
            .addMatcher(isPending(getMyChatRooms), (state) => {
            state.loading = true;
            state.errorMessage = null;
        })
            .addMatcher(isPending(getChatDirectoryUsers), (state) => {
            state.directoryLoading = true;
        })
            .addMatcher(isPending(createChatRoom, getOrCreateDmRoom), (state) => {
            state.creating = true;
            state.errorMessage = null;
        })
            .addMatcher(isPending(getChatRoomMembers), (state) => {
            state.membersLoading = true;
            state.errorMessage = null;
        })
            .addMatcher(isPending(deleteChatRoom, addChatRoomMember, removeChatRoomMember), (state) => {
            state.mutating = true;
            state.errorMessage = null;
        })
            .addMatcher(isRejected(getMyChatRooms, createChatRoom, getOrCreateDmRoom, getChatDirectoryUsers, deleteChatRoom, getChatRoomMembers, addChatRoomMember, removeChatRoomMember), (state, action) => {
            state.loading = false;
            state.creating = false;
            state.mutating = false;
            state.membersLoading = false;
            state.directoryLoading = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        });
    },
});
export const { reset, clearLastCreated, clearLastDeletedRoom, clearErrorMessage, applyInboundChatMessage, clearRoomUnread, upsertRoomFromMemberAdded, } = ChatRoomSlice.actions;
export default ChatRoomSlice.reducer;
